import { action, computed, makeObservable } from 'mobx';

import { UserStore } from '@/configs/store';
import { AlertsStore } from '@/configs/store/AlertsStore';
import { QueryStore } from '@/configs/store/QueryStore';
import { FavoriteUsersModel } from '@/features/favoriteUsers/FavoriteUsersModel';
import { getLogsStorageKey, getLogsUserId, LogsMode } from '@/features/logsMode';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { UserSuggestModel } from '@/models/UserSuggestModel';
import { ValueModel } from '@/models/ValueModel';
import { YandexUser } from '@/types/yandex';

import { UsersFilterStorageKey } from './constants';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface IGlobalUserFilterModel {
  // From UserSuggestModel
  suggestions: YandexUser[];
  suggestQuery: string;
  loadingStage: LoadingStageModel;
  showAll: boolean;
  options: YandexUser[];
  favoriteUsersModel: FavoriteUsersModel;

  setSuggestQuery: (query: string) => void;
  toggleShowAll: () => void;

  // Global filter specific
  selectedUser: YandexUser | null;
  selectedUserId: string | null;
  /** Id текущего пользователя в системе логов режима */
  myUserId: string | null;
  isViewingSelf: boolean;

  setSelectedUser: (userData: YandexUser | null) => void;
}

/**
 * Глобальная модель фильтра пользователей
 * используется для выбора пользователя, логи которого просматриваются в данный момент
 */
export class GlobalUserFilterModel implements IGlobalUserFilterModel {
  private readonly _suggestModel: UserSuggestModel;
  private readonly _selectedUserData = new ValueModel<YandexUser | null>(null);

  private readonly userStore: UserStore;
  private readonly queryStore: QueryStore;
  private readonly _mode: LogsMode;
  private readonly _storageKey: string;

  constructor({
    userStore,
    queryStore,
    alertsStore,
    favoriteUsersModel,
    mode,
  }: {
    userStore: UserStore;
    queryStore: QueryStore;
    alertsStore: AlertsStore;
    favoriteUsersModel: FavoriteUsersModel;
    mode: LogsMode;
  }) {
    this.userStore = userStore;
    this.queryStore = queryStore;
    this._mode = mode;
    this._storageKey = getLogsStorageKey({ key: UsersFilterStorageKey.SelectedUserId, mode });

    this._suggestModel = new UserSuggestModel(userStore, alertsStore, favoriteUsersModel, mode);

    makeObservable(this, {
      selectedUser: computed,
      selectedUserId: computed,
      myUserId: computed,
      isViewingSelf: computed,
      suggestions: computed,
      suggestQuery: computed,
      options: computed,
      loadingStage: computed,
      showAll: computed,
      setSelectedUser: action,
      setSuggestQuery: action,
      toggleShowAll: action,
    });

    this.initializeSelectedUser();
  }

  // прокси геттеры из UserSuggestModel
  get suggestions() {
    return this._suggestModel.suggestions;
  }

  get suggestQuery() {
    return this._suggestModel.suggestQuery;
  }

  get options() {
    return this._suggestModel.options;
  }

  get loadingStage() {
    return this._suggestModel.loadingStage;
  }

  get showAll() {
    return this._suggestModel.showAll;
  }

  get favoriteUsersModel() {
    return this._suggestModel.favoriteUsersModel;
  }

  // прокси методы из UserSuggestModel
  setSuggestQuery = (query: string) => {
    this._suggestModel.setSuggestQuery(query);
  };

  toggleShowAll = () => {
    this._suggestModel.toggleShowAll();
  };

  get selectedUser(): YandexUser | null {
    return this._selectedUserData.value;
  }

  get selectedUserId(): string | null {
    return this._selectedUserData.value?.uid ?? null;
  }

  get myUserId(): string | null {
    const user = this.userStore.user;

    return user ? getLogsUserId({ user, mode: this._mode }) : null;
  }

  get isViewingSelf(): boolean {
    return this.myUserId !== null && String(this.myUserId) === String(this.selectedUserId);
  }

  setSelectedUser = (userData: YandexUser | null) => {
    this._selectedUserData.change(userData);
    this.syncToUrlAndStorage(userData?.uid ?? null);
  };

  loadUserDetail = async (id: string): Promise<YandexUser> => {
    return this._suggestModel.loadUserDetail(id);
  };

  // синк юзера с URL и localStorage
  private syncToUrlAndStorage(userId: string | null) {
    this.queryStore.setParam('user', userId);

    if (userId) {
      localStorage.setItem(this._storageKey, userId);
    } else {
      localStorage.removeItem(this._storageKey);
    }
  }

  private initializeSelectedUser() {
    const urlUserId = this.queryStore.getParam('user');

    if (urlUserId) {
      this.restoreUserData(urlUserId);

      return;
    }

    const storedUserId = localStorage.getItem(this._storageKey);

    if (storedUserId) {
      this.restoreUserData(storedUserId);

      return;
    }

    // по умолчанию текущий юзер
    const user = this.userStore.user;
    const myUserId = this.myUserId;

    if (user && myUserId) {
      const currentUserData: YandexUser = {
        uid: myUserId,
        display: user.fullName,
        email: user.email,
      };
      this._selectedUserData.change(currentUserData);
    }
  }

  private async restoreUserData(userId: string) {
    const user = this.userStore.user;
    const currentYandexId = this.myUserId;

    // текущий ли юзер
    if (user && currentYandexId && currentYandexId === userId) {
      const currentUserData: YandexUser = {
        uid: currentYandexId,
        display: user.fullName,
        email: user.email,
      };
      this._selectedUserData.change(currentUserData);

      return;
    }

    // ?user= из другого режима: uid Яндекса не uuid — молча показываем себя
    const isForeignId = this._mode === LogsMode.v2 && !UUID.test(userId);

    try {
      if (isForeignId) throw new Error(`Not a v2 user id: ${userId}`);

      const userData = await this.loadUserDetail(userId);
      this._selectedUserData.change(userData);
    } catch (e) {
      console.warn('Failed to restore user data for ID:', userId, e);

      // на случай ошибки, fallback к текущему юзеру
      if (currentYandexId) {
        const currentUserData: YandexUser = {
          uid: currentYandexId,
          display: user!.fullName,
          email: user!.email,
        };
        this._selectedUserData.change(currentUserData);
      }
    }
  }
}
